import { Box } from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  InsightsChartSkeleton,
  InsightsEmptyState,
  InsightsErrorState,
} from "../../components/Insights/InsightsAsyncState";
import { chartHasSeriesData, pricingLineChartOptions } from "./ChartBlock";
import { decorateCombinedRetailerChartForDisplay } from "./pricingChartsUtils";
import { PRICING_REPORT_SCREEN_CHART_PLOT_HEIGHT_PX } from "./pricingReportPrintDimensions";

function buildCombinedRetailerChartOptions(onLegendFocus) {
  return {
    ...pricingLineChartOptions,
    plugins: {
      ...pricingLineChartOptions.plugins,
      legend: {
        ...pricingLineChartOptions.plugins.legend,
        onClick: (_event, legendItem) => {
          if (legendItem?.datasetIndex == null) return;
          onLegendFocus(legendItem.datasetIndex);
        },
      },
    },
  };
}

export default function CombinedRetailerChartBlock({
  chart,
  chartRef,
  isLoading = false,
  error = null,
  hasData: hasDataProp,
  emptyMessage = "No chart data available for the current filters.",
}) {
  const [focusedDatasetIndex, setFocusedDatasetIndex] = useState(null);

  useEffect(() => {
    setFocusedDatasetIndex(null);
  }, [chart]);

  const hasData =
    typeof hasDataProp === "boolean" ? hasDataProp : chartHasSeriesData(chart);

  const displayChart = useMemo(
    () => decorateCombinedRetailerChartForDisplay(chart, focusedDatasetIndex),
    [chart, focusedDatasetIndex]
  );

  const chartOptions = useMemo(
    () =>
      buildCombinedRetailerChartOptions((datasetIndex) => {
        setFocusedDatasetIndex((current) =>
          current === datasetIndex ? null : datasetIndex
        );
      }),
    []
  );

  const setChartRef = useCallback(
    (instance) => {
      if (typeof chartRef === "function") {
        chartRef(instance);
      } else if (chartRef && typeof chartRef === "object") {
        chartRef.current = instance;
      }
    },
    [chartRef]
  );

  if (error) {
    return (
      <InsightsErrorState
        message={error}
        fallbackMessage="Something went wrong while loading chart data."
      />
    );
  }

  if (isLoading) {
    return <InsightsChartSkeleton />;
  }

  if (!hasData) {
    return <InsightsEmptyState message={emptyMessage} />;
  }

  return (
    <Box
      data-pricing-chart-plot
      sx={{
        height: PRICING_REPORT_SCREEN_CHART_PLOT_HEIGHT_PX,
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Line ref={setChartRef} data={displayChart} options={chartOptions} />
    </Box>
  );
}

CombinedRetailerChartBlock.propTypes = {
  chart: PropTypes.object,
  chartRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  isLoading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  hasData: PropTypes.bool,
  emptyMessage: PropTypes.string,
};
